import React, {useContext} from 'react';

import {envIsLocal} from '../config';
import {
  OrganizationSubscriptionStatus,
  OrganizationSubscriptionType,
} from '../generated/graphql';
import {useGetIsAcademicUser} from '../pages/TrialEndPage/useGetIsAcademicUser';
import {isTrialPlanPrimaryPlan} from '../util/accounts/pricing';
import {differenceInDays} from '../util/date';
import {trialEndPage} from '../util/urls';
import {GlobalBannerContext} from './GlobalBanners/GlobalBannerContextProvider';
import {LegacyNudgeBarType} from './GlobalBanners/types';
import NudgeBar from './NudgeBar';

const TeamTrialNudgeBar: React.FC<{
  entityName: string;
}> = React.memo(({entityName}) => {
  const {states} = useContext(GlobalBannerContext);
  const isAcademicUser = useGetIsAcademicUser();

  if (envIsLocal || states == null || states.length === 0) {
    return null;
  }

  const hasGlobalBanner = states.some(
    state =>
      state.renderedNudgeBar === LegacyNudgeBarType.EnterpriseToStandard ||
      state.renderedNudgeBar === LegacyNudgeBarType.EnterpriseToAdvanced
  );

  const stateWithOrganization = states.find(state =>
    state.organization?.teams.some(team => team.name === entityName)
  );

  if (stateWithOrganization == null) {
    return null;
  }
  const teamOrg = stateWithOrganization?.organization;

  // TODO(haruka)?: add subscriptionType === ManualTrial for enterprise trial
  const userLedSubscription = teamOrg?.subscriptions?.find(
    sub =>
      sub.status === OrganizationSubscriptionStatus.Enabled &&
      sub.subscriptionType === OrganizationSubscriptionType.UserLedTrial
  );

  if (userLedSubscription == null) {
    return null;
  }

  const userLedPlan = userLedSubscription.plan;

  if (userLedPlan == null) {
    return null;
  }

  const isReverseTrial = isTrialPlanPrimaryPlan(userLedPlan);

  if (isReverseTrial && isAcademicUser) {
    // hide nudge bar for academic users on reverse trial
    return null;
  }

  if (
    hasGlobalBanner ||
    userLedSubscription.expiresAt == null ||
    new Date(userLedSubscription.expiresAt) <= new Date()
  ) {
    return null;
  }

  const expiresAt = new Date(userLedSubscription.expiresAt);
  // converts the dates into ms since epoch and then convert the time from ms to days
  const daysRemaining = differenceInDays(expiresAt, new Date());

  return (
    <NudgeBar
      inline
      content={`${daysRemaining} days remaining in your team trial.`}
      trackingLocation={'team trial nudge bar'}
      linkText={'Learn more →'}
      link={trialEndPage()}
    />
  );
});

export default TeamTrialNudgeBar;
